import { FC } from 'react';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';

export interface TabProps {
  route: string;
  name: string;
  count?: number;
  isLastTab?: boolean;
  isPills?: boolean;
  /** When true, tab is not clickable and styled as disabled. Route remains valid for direct navigation. */
  disabled?: boolean;
  /** Optional badge (e.g. "Coming soon") shown in upper right of tab label. */
  badge?: string;
}

const Tab: FC<TabProps> = ({
  route,
  name,
  count,
  isLastTab,
  isPills,
  disabled = false,
  badge,
}) => {
  const content = (isActive: boolean) => {
    const defaultActive = isActive && !isPills;
    return (
      <>
        <div
          className={classNames(
            defaultActive && 'pt-[2px]',
            !isPills && 'mb-2 px-2',
            isPills && 'px-0',
            'relative flex flex-col items-center justify-between',
          )}
        >
          <div
            className={classNames(
              'relative flex flex-row items-center justify-between px-3',
              !isPills && 'rounded-xl py-2 hover:bg-neutral-25',
              isPills &&
                'rounded-full border border-transparent bg-neutral-50 px-4 py-1.5',
              isPills && !isActive && !disabled && 'hover:bg-neutral-75',
              isPills &&
                isActive &&
                !disabled &&
                'border-green-100 bg-green-25 hover:bg-green-25',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <span
              className={classNames(
                'whitespace-nowrap font-sans text-caption text-neutral-150',
                isActive && !disabled && 'text-green-150',
              )}
            >
              {name}
            </span>
            {badge && (
              <span className="text-neutral-120 ml-2 shrink-0 rounded bg-neutral-75 px-1.5 py-0.5 text-[10px] font-medium">
                {badge}
              </span>
            )}
            {count !== undefined && !badge && (
              <div
                className={classNames(
                  'flex flex-row items-center justify-center rounded-full text-neutral-150',
                  !isPills && 'min-h-6 min-w-6 ml-3 bg-neutral-100/30 p-1 px-2',
                  !isPills && isActive && 'bg-green-25',
                  isPills && 'ml-2 pt-[1px] text-green-150',
                )}
              >
                <span className="text-small-caption font-bold">{count}</span>
              </div>
            )}
          </div>
        </div>
        {defaultActive && (
          <div className="relative top-[1px] w-full border-t-2 border-secondary-100" />
        )}
      </>
    );
  };

  if (disabled) {
    return (
      <span
        className={classNames(
          !isLastTab && !isPills && 'mr-6',
          !isLastTab && isPills && 'mr-2',
          'pointer-events-none rounded-full focus:outline-none',
        )}
        aria-disabled="true"
      >
        {content(false)}
      </span>
    );
  }

  return (
    <NavLink
      to={route}
      className={classNames(
        !isLastTab && !isPills && 'mr-6',
        !isLastTab && isPills && 'mr-2',
        'rounded-full focus:outline-none',
      )}
      end={route === '.'}
    >
      {({ isActive }) => content(isActive)}
    </NavLink>
  );
};

export default Tab;
